import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "../../api";
import { SectionTitle, StatusPill, EmptyState, stripHtml } from "../../components/Atoms";
import { Briefcase, Globe, EnvelopeSimple, Phone, Stack, ArrowLeft, Calendar, ArrowRight } from "@phosphor-icons/react";

export default function AdminClientDetailPage() {
  const { cid } = useParams();
  const nav = useNavigate();
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([
          api.get(`/clients/${cid}`),
          api.get(`/projects`),
        ]);
        setClient(c.data);
        setProjects((p.data || []).filter(pr => pr.client_id === cid));
      } finally { setLoading(false); }
    })();
  }, [cid]);

  if (loading) return <div className="skeleton h-64" />;
  if (!client) return <EmptyState title="Client not found" />;

  return (
    <div className="space-y-5">
      <button onClick={() => nav("/admin/clients")} className="btn-ghost flex items-center gap-1.5 text-sm" data-testid="back-to-clients">
        <ArrowLeft size={14} /> Back to clients
      </button>

      {/* Client header */}
      <div className="glass p-5">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#22D3EE] to-[#8B5CF6] grid place-items-center overflow-hidden flex-shrink-0">
            {client.logo ? <img src={client.logo} alt={client.company} className="w-full h-full object-cover" /> :
              <Briefcase size={24} weight="fill" color="white" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-white text-xl font-bold truncate">{client.company}</div>
              <StatusPill status={client.status} />
            </div>
            <div className="text-xs text-muted mt-1">{client.industry || "—"} · {client.contact_person || "—"}</div>
            <div className="mt-3 flex flex-wrap gap-3 text-sm text-muted">
              {client.email && <div className="flex items-center gap-1.5"><EnvelopeSimple size={14} /> {client.email}</div>}
              {client.phone && <div className="flex items-center gap-1.5"><Phone size={14} /> {client.phone}</div>}
              {client.website && <div className="flex items-center gap-1.5"><Globe size={14} /> {client.website}</div>}
            </div>
          </div>
        </div>
      </div>

      <SectionTitle title={`Projects (${projects.length})`} subtitle={`Campaigns run by ${client.company}`} />
      {projects.length === 0 ? <EmptyState icon={Stack} title="No projects for this client yet." /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map(p => (
            <Link key={p.id} to={`/admin/projects/${p.id}`} className="glass p-5 hover-lift block" data-testid={`client-project-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="text-white font-bold truncate">{p.title}</div>
                <StatusPill status={p.status} />
              </div>
              <div className="text-xs text-muted truncate">{p.category || "—"}</div>
              <div className="text-sm text-muted line-clamp-2 mt-3">{stripHtml(p.description)}</div>
              <div className="grid grid-cols-3 gap-2 text-xs mt-3">
                <div className="bg-white/5 rounded-xl px-3 py-2"><div className="text-muted">Tasks</div><div className="text-white font-bold">{p.task_count || 0}</div></div>
                <div className="bg-white/5 rounded-xl px-3 py-2"><div className="text-muted">Creators</div><div className="text-white font-bold">{p.ambassador_count || 0}</div></div>
                <div className="bg-white/5 rounded-xl px-3 py-2"><div className="text-muted">Reward</div><div className="text-white font-bold">{fmtMoney(p.total_reward || 0)}</div></div>
              </div>
              <div className="text-[10px] text-muted mt-3 flex items-center justify-between">
                <span className="flex items-center gap-1"><Calendar size={12} /> {fmtDate(p.start_date)} → {fmtDate(p.end_date)}</span>
                <span className="text-[#22D3EE] flex items-center gap-1 font-bold">Open <ArrowRight size={12} weight="bold" /></span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
